import { useMemo, useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
    ArrowRight, ArrowUpRight, MessageCircle, MapPin, Users, TrendingUp,
    Sparkles, Sun, Clock, Snowflake, Flame, Coffee, IceCream, Candy, Donut,
    CroissantIcon, Wine, Newspaper, Sandwich, Fish, ChefHat, Zap, Ruler,
    Package, Award, Quote, Umbrella,
} from "lucide-react";
import CtaBanner from "@/components/CtaBanner";
import Reveal from "@/components/Reveal";
import { useLocale } from "@/context/LocaleContext";
import { useSetting } from "@/context/CmsContext";
import { api } from "@/lib/api";
import useSeo from "@/lib/useSeo";

/* ---------- IMAGE HELPERS ---------- */
// All images live locally in /brand/. Same NOVIFOOD cart model, different
// canopy/equipment/branding — generated from the reference photo. This keeps
// visual continuity between the 9 niche landing pages.
const withV = (path) => `${path}?v=2`;

/* ---------- NICHES DATA (9 landing pages) ---------- */
const NICHES = {
    doces: {
        icon: Candy,
        primary: "#DB2777",
        accent: "#FBCFE8",
        soft: "#FDF2F8",
        label_pt: "Doces & pastelaria",
        label_en: "Sweets & pastry",
        eyebrow_pt: "Nicho de mercado · Doces",
        eyebrow_en: "Market niche · Sweets",
        title_pt: ["Doçaria que ", "conquista", " qualquer rua."],
        title_en: ["Confectionery that ", "wins over", " any street."],
        subtitle_pt: "Bolachas, brownies, cupcakes e docinhos artesanais servidos com estética de padaria francesa. Ideal para centros históricos, mercados temáticos e eventos com famílias.",
        subtitle_en: "Cookies, brownies, cupcakes and artisan sweets served with a French bakery aesthetic. Perfect for historic centres, themed markets and family events.",
        images: {
            hero: "/brand/tema-pipocas-branded.jpg",
            branded: "/brand/tema-pipocas-branded.jpg",
            product: "/brand/tema-pipocas.jpg",
            no_canopy: "/brand/tema-doces-no-canopy.jpg",
        },
        audience_pt: "Famílias, turistas de passeio, escritórios e eventos infantis.",
        audience_en: "Families, strolling tourists, office crowds and children's events.",
        advantages: [
            {
                icon: TrendingUp,
                title_pt: "Margens elevadas",
                title_en: "High margins",
                desc_pt: "Custo por unidade baixo e preço percebido alto — 200–350% de margem por doce artesanal.",
                desc_en: "Low cost per unit and high perceived price — 200–350% margin per artisan sweet.",
            },
            {
                icon: Sun,
                title_pt: "Todo o ano",
                title_en: "Year-round",
                desc_pt: "Sem sazonalidade forte: aniversários, festas, escolas, mercados de Natal e verão.",
                desc_en: "No strong seasonality: birthdays, parties, schools, Christmas and summer markets.",
            },
            {
                icon: Sparkles,
                title_pt: "Vitrine que vende",
                title_en: "Display that sells",
                desc_pt: "Bancada iluminada + display frontal com bolachas coloridas atrai passagem espontânea.",
                desc_en: "Lit counter + front display with colourful cookies draws passing traffic naturally.",
            },
        ],
        equipment_pt: ["Vitrine expositora refrigerada", "Iluminação warm quente 3000K", "Bancada inox 90 cm", "Cones/sacos kraft branded"],
        equipment_en: ["Refrigerated display case", "Warm 3000K lighting", "90 cm stainless counter", "Branded kraft cones/bags"],
        margin: "€55 – €140",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Chiado & Baixa (Lisboa)", "Ribeira (Porto)", "Praças com feiras semanais", "Centros comerciais outdoor"],
        locations_en: ["Chiado & Baixa (Lisbon)", "Ribeira (Porto)", "Weekly market squares", "Outdoor shopping centres"],
        ideal_model: "alfa",
        seo_desc_pt: "Landing dedicada ao nicho de doces e pastelaria: carrinho NOVIFOOD com vitrine refrigerada, iluminação warm e bancada inox. Margens 200–350%.",
        seo_desc_en: "Dedicated landing for the sweets and pastry niche: NOVIFOOD cart with refrigerated display, warm lighting and stainless counter. 200–350% margins.",
    },
    gelados: {
        icon: IceCream,
        primary: "#0891B2",
        accent: "#A5F3FC",
        soft: "#ECFEFF",
        label_pt: "Gelados artesanais",
        label_en: "Artisan gelato",
        eyebrow_pt: "Nicho de mercado · Gelados",
        eyebrow_en: "Market niche · Gelato",
        title_pt: ["Gelado italiano com ", "autonomia", " total."],
        title_en: ["Italian gelato with ", "full autonomy", "."],
        subtitle_pt: "Vitrines de 6 a 9 cubas com -18°C mantidas por bateria plus + painel solar. Instale à beira-mar, num parque ou num festival sem depender da rede eléctrica.",
        subtitle_en: "6-to-9-tub displays at -18°C powered by plus battery + solar panel. Set up seafront, in a park or at a festival without relying on the grid.",
        images: {
            hero: "/brand/tema-gelados-branded.jpg",
            branded: "/brand/tema-gelados-branded.jpg",
            product: "/brand/tema-gelados.jpg",
            no_canopy: "/brand/tema-gelados-no-canopy.jpg",
        },
        audience_pt: "Turistas na marginal, famílias em parques, festivais de verão e mercados.",
        audience_en: "Seafront tourists, park-going families, summer festivals and markets.",
        advantages: [
            {
                icon: Snowflake,
                title_pt: "-18°C sem fio",
                title_en: "-18°C wireless",
                desc_pt: "Bateria plus 10h + painel solar mantêm a vitrine mesmo em locais sem tomada.",
                desc_en: "Plus battery 10h + solar panel keep the display running even in outlet-free spots.",
            },
            {
                icon: TrendingUp,
                title_pt: "Ticket médio alto",
                title_en: "High average ticket",
                desc_pt: "Gelato €3,50–€6 vs. custo €0,80/unidade. ROI de 4–6 meses num verão.",
                desc_en: "Gelato €3.50–€6 vs. €0.80/unit cost. 4–6 month ROI in one summer season.",
            },
            {
                icon: Sun,
                title_pt: "Verão explosivo",
                title_en: "Explosive summer",
                desc_pt: "Junho a Setembro rendem 60% do ano — modelo compacto Alfa é operado por 1 pessoa.",
                desc_en: "June to September yield 60% of the year — compact Alfa runs with 1 person.",
            },
        ],
        equipment_pt: ["Vitrine curva 6 cubas", "Bateria plus (10h)", "Painel solar 200W", "Congelador auxiliar -18°C"],
        equipment_en: ["Curved 6-tub display", "Plus battery (10h)", "200W solar panel", "-18°C auxiliary freezer"],
        margin: "€60 – €150",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Marginal de Cascais/Estoril", "Praças de aldeia no Verão", "Festivais e concertos", "Parques urbanos"],
        locations_en: ["Cascais/Estoril seafront", "Village squares in summer", "Festivals and concerts", "Urban parks"],
        ideal_model: "alfa",
        seo_desc_pt: "Nicho de gelato artesanal: vitrine 6 cubas -18°C com bateria plus e painel solar. Autonomia total à beira-mar. Margem €60–€150/dia.",
        seo_desc_en: "Artisan gelato niche: 6-tub -18°C display with plus battery and solar panel. Full seafront autonomy. €60–€150/day margin.",
    },
    churros: {
        icon: Donut,
        primary: "#C2410C",
        accent: "#FED7AA",
        soft: "#FFF7ED",
        label_pt: "Churros",
        label_en: "Churros",
        eyebrow_pt: "Nicho de mercado · Churros",
        eyebrow_en: "Market niche · Churros",
        title_pt: ["Churros ", "quentinhos", " onde a fila se forma."],
        title_en: ["Churros ", "piping hot", " where the queue forms."],
        subtitle_pt: "Fritadeira profissional integrada com extractor, dosificador de massa e balcão frontal para dulce de leche e chocolate. Volume alto em festas populares e feiras de Natal.",
        subtitle_en: "Integrated professional fryer with extractor, dough dispenser and front counter for dulce de leche and chocolate. High volume at festivals and Christmas fairs.",
        images: {
            hero: "/brand/tema-pipocas-branded.jpg",
            branded: "/brand/tema-pipocas-branded.jpg",
            product: "/brand/tema-pipocas.jpg",
            no_canopy: "/brand/tema-churros-no-canopy.jpg",
        },
        audience_pt: "Público jovem-adulto, famílias em feiras, romarias e mercados de Natal.",
        audience_en: "Young-adult crowds, families at fairs, festivals and Christmas markets.",
        advantages: [
            {
                icon: Flame,
                title_pt: "Fritura profissional",
                title_en: "Professional frying",
                desc_pt: "Fritadeira 8L com extractor e filtro anti-fumos integrado no teto retráctil.",
                desc_en: "8L fryer with extractor and anti-smoke filter built into the retractable roof.",
            },
            {
                icon: TrendingUp,
                title_pt: "Rotação altíssima",
                title_en: "Very high rotation",
                desc_pt: "Cucurucho a €3–€5 servido em 45 segundos. 80+ vendas/hora no pico.",
                desc_en: "Cone at €3–€5 served in 45 seconds. 80+ sales/hour at peak.",
            },
            {
                icon: Users,
                title_pt: "Impulso emocional",
                title_en: "Emotional impulse",
                desc_pt: "Cheiro a canela e açúcar puxa clientes de longa distância. Marketing gratuito.",
                desc_en: "Cinnamon-sugar aroma pulls customers from far away. Free marketing.",
            },
        ],
        equipment_pt: ["Fritadeira profissional 8L", "Extractor + filtro", "Dosificador de massa", "Bancada de recheios"],
        equipment_en: ["Professional 8L fryer", "Extractor + filter", "Dough dispenser", "Filling counter"],
        margin: "€65 – €160",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Feiras populares & romarias", "Mercados de Natal", "Praias de bandeira azul", "Festivais de verão"],
        locations_en: ["Popular fairs & festivals", "Christmas markets", "Blue Flag beaches", "Summer festivals"],
        ideal_model: "omega",
        seo_desc_pt: "Nicho churros: carrinho com fritadeira profissional 8L, extractor e dosificador. Vendas até 80/hora em feiras. Margem €65–€160/dia.",
        seo_desc_en: "Churros niche: cart with 8L professional fryer, extractor and dispenser. Up to 80 sales/hour at fairs. €65–€160/day margin.",
    },
    cafes: {
        icon: Coffee,
        primary: "#78350F",
        accent: "#FCD34D",
        soft: "#FFFBEB",
        label_pt: "Cafés & takeaway",
        label_en: "Coffee & takeaway",
        eyebrow_pt: "Nicho de mercado · Cafés",
        eyebrow_en: "Market niche · Coffee",
        title_pt: ["O café da manhã, ", "onde ela passa", "."],
        title_en: ["Morning coffee, ", "where she walks by", "."],
        subtitle_pt: "Máquina de espresso 2 grupos com moinho profissional, bancada de leites vegetais e display de bolos. O carrinho ideal para o rush das 8h e do pós-almoço.",
        subtitle_en: "2-group espresso machine with professional grinder, plant-milk counter and cake display. The ideal cart for the 8am and post-lunch rush.",
        images: {
            hero: "/brand/tema-cafes-branded.jpg",
            branded: "/brand/tema-cafes-branded.jpg",
            product: "/brand/tema-cafes.jpg",
            no_canopy: "/brand/tema-cafes-no-canopy.jpg",
        },
        audience_pt: "Profissionais em trânsito, universitários, hospitais, estações e escritórios.",
        audience_en: "Commuters, university students, hospitals, stations and offices.",
        advantages: [
            {
                icon: Clock,
                title_pt: "Alta rotatividade",
                title_en: "Fast turnover",
                desc_pt: "Espresso servido em 25 segundos. 120–180 cafés por manhã com 1 pessoa a operar.",
                desc_en: "Espresso served in 25 seconds. 120–180 coffees per morning with 1 operator.",
            },
            {
                icon: TrendingUp,
                title_pt: "Fidelização diária",
                title_en: "Daily loyalty",
                desc_pt: "Café + croissant a €3,20 com margem 68%. Cliente volta 3x/semana em média.",
                desc_en: "Coffee + croissant at €3.20 with 68% margin. Customer returns 3x/week on average.",
            },
            {
                icon: Zap,
                title_pt: "Baixo consumo",
                title_en: "Low power draw",
                desc_pt: "Máquina de 1,8 kW compatível com bateria plus + tomada trifásica de reserva.",
                desc_en: "1.8 kW machine compatible with plus battery + backup three-phase outlet.",
            },
        ],
        equipment_pt: ["Máquina 2 grupos", "Moinho profissional", "Bancada de leites & syrups", "Display de bolos & vitrine"],
        equipment_en: ["2-group espresso machine", "Professional grinder", "Milks & syrups counter", "Cakes display & showcase"],
        margin: "€45 – €120",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Junto a estações de metro/comboio", "Universidades e hospitais", "Business parks", "Praças de escritórios"],
        locations_en: ["Metro/train stations", "Universities and hospitals", "Business parks", "Office squares"],
        ideal_model: "alfa",
        seo_desc_pt: "Nicho café takeaway: carrinho com máquina 2 grupos e moinho profissional. 120–180 cafés por manhã. Margem €45–€120/dia.",
        seo_desc_en: "Coffee takeaway niche: cart with 2-group espresso and professional grinder. 120–180 coffees per morning. €45–€120/day margin.",
    },
    paes: {
        icon: CroissantIcon,
        primary: "#92400E",
        accent: "#FDE68A",
        soft: "#FEFCE8",
        label_pt: "Pães & padaria",
        label_en: "Bakery & bread",
        eyebrow_pt: "Nicho de mercado · Padaria",
        eyebrow_en: "Market niche · Bakery",
        title_pt: ["O ", "cheiro a pão", " que vende sozinho."],
        title_en: ["The ", "bread scent", " that sells itself."],
        subtitle_pt: "Padaria móvel com forno de aquecimento, prateleiras de exposição e display de pastelaria fresca. Instale-se ao pé de mercados matinais e turnos de saída de escritórios.",
        subtitle_en: "Mobile bakery with heating oven, display shelves and fresh pastry showcase. Set up next to morning markets and office-exit shifts.",
        images: {
            hero: "/brand/tema-paes-branded.jpg",
            branded: "/brand/tema-paes-branded.jpg",
            product: "/brand/tema-paes.jpg",
            no_canopy: "/brand/tema-paes-no-canopy.jpg",
        },
        audience_pt: "Residentes locais, famílias ao pequeno-almoço, feiras semanais e escritórios.",
        audience_en: "Local residents, breakfast families, weekly fairs and offices.",
        advantages: [
            {
                icon: Flame,
                title_pt: "Forno integrado",
                title_en: "Built-in oven",
                desc_pt: "Regenera pão e croissants no local. Cheiro atrai clientes num raio de 30 m.",
                desc_en: "Reheats bread and croissants on the spot. Aroma pulls customers within 30 m.",
            },
            {
                icon: Users,
                title_pt: "Rotina de bairro",
                title_en: "Neighbourhood habit",
                desc_pt: "Clientes voltam 5x por semana. LTV muito superior a nichos de impulso.",
                desc_en: "Customers return 5x a week. LTV far higher than impulse-driven niches.",
            },
            {
                icon: Package,
                title_pt: "Cabaz combo",
                title_en: "Combo basket",
                desc_pt: "Pão + café + pastel a €4,50 aumenta ticket médio em 40% face a produto único.",
                desc_en: "Bread + coffee + pastry at €4.50 lifts average ticket by 40% vs. single product.",
            },
        ],
        equipment_pt: ["Forno de aquecimento", "Prateleiras de exposição", "Vitrine de pastelaria", "Cestos artesanais"],
        equipment_en: ["Heating oven", "Display shelves", "Pastry showcase", "Artisan baskets"],
        margin: "€50 – €130",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Junto a mercados matinais", "Bairros residenciais", "Parques empresariais", "Feiras de produtores"],
        locations_en: ["Next to morning markets", "Residential areas", "Business parks", "Producer fairs"],
        ideal_model: "omega",
        seo_desc_pt: "Nicho padaria móvel: carrinho com forno de aquecimento, prateleiras e vitrine. Rotina de bairro com LTV alto. Margem €50–€130/dia.",
        seo_desc_en: "Mobile bakery niche: cart with heating oven, shelves and showcase. Neighbourhood routine with high LTV. €50–€130/day margin.",
    },
    drinks: {
        icon: Wine,
        primary: "#6B21A8",
        accent: "#E9D5FF",
        soft: "#FAF5FF",
        label_pt: "Drinks & cocktails",
        label_en: "Drinks & cocktails",
        eyebrow_pt: "Nicho de mercado · Drinks",
        eyebrow_en: "Market niche · Drinks",
        title_pt: ["O bar móvel para ", "cada evento", "."],
        title_en: ["The mobile bar for ", "every event", "."],
        subtitle_pt: "Cocktails de autor, sumos naturais e mocktails servidos com estilo. Configuração pro para ativações de marca, casamentos, corporate e festivais.",
        subtitle_en: "Signature cocktails, fresh juices and mocktails served with style. Pro setup for brand activations, weddings, corporate events and festivals.",
        images: {
            hero: "/brand/tema-drinks-branded.jpg",
            branded: "/brand/tema-drinks-branded.jpg",
            product: "/brand/tema-drinks.jpg",
            no_canopy: "/brand/tema-drinks-no-canopy.jpg",
        },
        audience_pt: "Marcas em ativação, agências de eventos, casamentos, corporate e festivais.",
        audience_en: "Brands running activations, event agencies, weddings, corporate and festivals.",
        advantages: [
            {
                icon: Award,
                title_pt: "Ticket premium",
                title_en: "Premium ticket",
                desc_pt: "Cocktail a €9–€14 num evento corporate. Cachet + gorjeta duplicam faturação.",
                desc_en: "Cocktail at €9–€14 at a corporate event. Booking fee + tips double revenue.",
            },
            {
                icon: Sparkles,
                title_pt: "Branding integrado",
                title_en: "Integrated branding",
                desc_pt: "Backdrop iluminado, copos personalizados e canópia com logo do cliente.",
                desc_en: "Lit backdrop, custom glasses and canopy with the client's logo.",
            },
            {
                icon: Users,
                title_pt: "2 bartenders",
                title_en: "2 bartenders",
                desc_pt: "Modelo Ômega permite operação simultânea — 120–200 drinks/hora em pico.",
                desc_en: "Ômega model allows simultaneous operation — 120–200 drinks/hour at peak.",
            },
        ],
        equipment_pt: ["Balcão duplo Ômega", "Frigorífico under-counter", "Ice-well & guarnições", "Backdrop iluminado"],
        equipment_en: ["Ômega double counter", "Under-counter fridge", "Ice-well & garnish", "Lit backdrop"],
        margin: "€120 – €400",
        margin_hint_pt: "margem/evento",
        margin_hint_en: "margin/event",
        locations_pt: ["Casamentos & eventos privados", "Ativações de marca", "Festivais & concertos", "Corporate & lançamentos"],
        locations_en: ["Weddings & private events", "Brand activations", "Festivals & concerts", "Corporate & launches"],
        ideal_model: "omega",
        seo_desc_pt: "Nicho bar móvel: Ômega com 2 bartenders, frigorífico under-counter, ice-well e backdrop iluminado. 120–200 drinks/hora. Margem €120–€400/evento.",
        seo_desc_en: "Mobile bar niche: Ômega with 2 bartenders, under-counter fridge, ice-well and lit backdrop. 120–200 drinks/hour. €120–€400/event margin.",
    },
    jornais: {
        icon: Newspaper,
        primary: "#1E3A5F",
        accent: "#DBEAFE",
        soft: "#EFF6FF",
        label_pt: "Jornais & revistas",
        label_en: "News & magazines",
        eyebrow_pt: "Nicho de mercado · Jornais",
        eyebrow_en: "Market niche · Newsstand",
        title_pt: ["O quiosque que ", "reinventa", " a esquina."],
        title_en: ["The kiosk that ", "reinvents", " the corner."],
        subtitle_pt: "Formato híbrido: jornais, revistas, tabaco, carregamentos e café takeaway. Concessão de espaço público com licença municipal simplificada.",
        subtitle_en: "Hybrid format: newspapers, magazines, tobacco, top-ups and takeaway coffee. Public space concession with simplified municipal licence.",
        images: {
            hero: "/brand/tema-jornais-branded.jpg",
            branded: "/brand/tema-jornais-branded.jpg",
            product: "/brand/tema-jornais.jpg",
            no_canopy: "/brand/tema-jornais-no-canopy.jpg",
        },
        audience_pt: "Residentes do bairro, executivos matinais e turistas em zonas de passagem.",
        audience_en: "Neighbourhood residents, morning commuters and tourists in transit zones.",
        advantages: [
            {
                icon: Clock,
                title_pt: "Fluxo constante",
                title_en: "Steady flow",
                desc_pt: "Aberto 7h–20h. Ticket baixo mas rotação diária estável, sem picos sazonais.",
                desc_en: "Open 7am–8pm. Low ticket but stable daily rotation, no seasonal peaks.",
            },
            {
                icon: Package,
                title_pt: "Múltiplas categorias",
                title_en: "Multiple categories",
                desc_pt: "Impressa + tabaco + carregamentos + café diluem risco de descida de vendas.",
                desc_en: "Print + tobacco + top-ups + coffee dilute the risk of sales decline.",
            },
            {
                icon: MapPin,
                title_pt: "Concessão pública",
                title_en: "Public concession",
                desc_pt: "Espaço em ruas & praças com licença municipal — barreira de entrada protegida.",
                desc_en: "Street & square space with municipal licence — protected entry barrier.",
            },
        ],
        equipment_pt: ["Prateleiras verticais", "Vitrine trancada com fechadura", "Terminal multibanco integrado", "Balcão para café takeaway"],
        equipment_en: ["Vertical shelving", "Locked display case", "Integrated payment terminal", "Takeaway coffee counter"],
        margin: "€40 – €95",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Esquinas de bairro histórico", "Praças com fluxo de metro", "Junto a estações & terminais", "Zonas turísticas"],
        locations_en: ["Historic-neighbourhood corners", "Squares near metro flow", "By stations & terminals", "Tourist zones"],
        ideal_model: "alfa",
        seo_desc_pt: "Nicho quiosque de jornais híbrido: prateleiras verticais, vitrine trancada, café takeaway. Concessão pública, fluxo estável. Margem €40–€95/dia.",
        seo_desc_en: "Hybrid newsstand niche: vertical shelving, locked case, takeaway coffee. Public concession, stable flow. €40–€95/day margin.",
    },
    sanduiches: {
        icon: Sandwich,
        primary: "#EA580C",
        accent: "#FED7AA",
        soft: "#FFF7ED",
        label_pt: "Sanduíches & deli",
        label_en: "Sandwiches & deli",
        eyebrow_pt: "Nicho de mercado · Sanduíches",
        eyebrow_en: "Market niche · Sandwiches",
        title_pt: ["Sanduíches ", "gourmet", ", almoço em 4 minutos."],
        title_en: ["Gourmet sandwiches, ", "lunch in", " 4 minutes."],
        subtitle_pt: "Plancha inox 60 cm, tostadeira profissional, frigorífico under-counter e display de charcuteria. Servir 60+ almoços entre as 12h e as 14h.",
        subtitle_en: "60 cm stainless plancha, professional toaster, under-counter fridge and charcuterie display. Serve 60+ lunches between 12–2pm.",
        images: {
            hero: "/brand/tema-cachorros-branded.jpg",
            branded: "/brand/tema-cachorros-branded.jpg",
            product: "/brand/tema-cachorros.jpg",
            no_canopy: "/brand/tema-cachorros-no-canopy.jpg",
        },
        audience_pt: "Trabalhadores em pausa de almoço, universitários e eventos desportivos.",
        audience_en: "Lunchtime workers, university students and sports events.",
        advantages: [
            {
                icon: Flame,
                title_pt: "Cozinha completa",
                title_en: "Full kitchen",
                desc_pt: "Plancha 60cm + tostadeira + frigorífico dão flexibilidade para menu de 8–12 opções.",
                desc_en: "60cm plancha + toaster + fridge deliver flexibility for an 8–12 item menu.",
            },
            {
                icon: Clock,
                title_pt: "Pico de almoço",
                title_en: "Lunch peak",
                desc_pt: "Foco 12h–14h30 rende 70% da faturação. Ideal para 1 operador + 1 apoio.",
                desc_en: "12–2:30pm focus yields 70% of revenue. Ideal for 1 operator + 1 helper.",
            },
            {
                icon: TrendingUp,
                title_pt: "Ticket saudável",
                title_en: "Healthy ticket",
                desc_pt: "Sanduíche premium a €7,50 + bebida €2 = €9,50 ticket médio, 62% de margem.",
                desc_en: "Premium sandwich at €7.50 + drink €2 = €9.50 average ticket, 62% margin.",
            },
        ],
        equipment_pt: ["Plancha inox 60 cm", "Tostadeira profissional", "Frigorífico under-counter", "Vitrine de charcutaria"],
        equipment_en: ["60 cm stainless plancha", "Professional toaster", "Under-counter fridge", "Charcuterie display"],
        margin: "€70 – €180",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Parques empresariais", "Universidades & IPSS", "Estádios & pavilhões", "Eventos desportivos"],
        locations_en: ["Business parks", "Universities", "Stadiums & arenas", "Sports events"],
        ideal_model: "omega",
        seo_desc_pt: "Nicho sanduíches gourmet: Ômega com plancha 60cm, tostadeira, frigorífico. 60+ almoços/pico com 62% margem. Ticket €9,50.",
        seo_desc_en: "Gourmet sandwich niche: Ômega with 60cm plancha, toaster, fridge. 60+ lunches/peak at 62% margin. €9.50 ticket.",
    },
    sushis: {
        icon: Fish,
        primary: "#047857",
        accent: "#D1FAE5",
        soft: "#ECFDF5",
        label_pt: "Sushi & poke",
        label_en: "Sushi & poke",
        eyebrow_pt: "Nicho de mercado · Sushi",
        eyebrow_en: "Market niche · Sushi",
        title_pt: ["Sushi ", "fresco", " onde as pessoas estão."],
        title_en: ["Fresh sushi ", "where", " people are."],
        subtitle_pt: "Bancada refrigerada -4°C, tábua de corte inox, cuba de arroz shari e display frontal. Opera em mercados gourmet, food halls e eventos corporate premium.",
        subtitle_en: "-4°C refrigerated counter, stainless cutting board, shari rice tub and front display. Operates in gourmet markets, food halls and premium corporate events.",
        images: {
            hero: "/brand/tema-sushis-branded.jpg",
            branded: "/brand/tema-sushis-branded.jpg",
            product: "/brand/tema-sushis.jpg",
            no_canopy: "/brand/tema-sushis-no-canopy.jpg",
        },
        audience_pt: "Público urbano premium, food halls, corporate lunches e casamentos.",
        audience_en: "Premium urban crowd, food halls, corporate lunches and weddings.",
        advantages: [
            {
                icon: Snowflake,
                title_pt: "Bancada -4°C",
                title_en: "-4°C counter",
                desc_pt: "Refrigeração dupla mantém peixe fresco 8h+ com bateria plus + solar.",
                desc_en: "Dual refrigeration keeps fish fresh 8h+ with plus battery + solar.",
            },
            {
                icon: Award,
                title_pt: "Ticket premium",
                title_en: "Premium ticket",
                desc_pt: "Poke bowl €12–€16, combinado 12 peças €14–€18. Margem líquida acima de 55%.",
                desc_en: "Poke bowl €12–€16, 12-piece combo €14–€18. Net margin above 55%.",
            },
            {
                icon: ChefHat,
                title_pt: "Show cooking",
                title_en: "Show cooking",
                desc_pt: "Corte à vista aumenta perceção de qualidade e permite preço 20% acima do delivery.",
                desc_en: "In-view cutting raises quality perception and allows 20% price above delivery.",
            },
        ],
        equipment_pt: ["Bancada refrigerada -4°C", "Tábua de corte inox", "Cuba shari + termómetro", "Display frontal 4 níveis"],
        equipment_en: ["-4°C refrigerated counter", "Stainless cutting board", "Shari tub + thermometer", "4-level front display"],
        margin: "€90 – €280",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Food halls & mercados gourmet", "Business parks premium", "Casamentos & corporate", "Eventos VIP"],
        locations_en: ["Food halls & gourmet markets", "Premium business parks", "Weddings & corporate", "VIP events"],
        ideal_model: "omega",
        seo_desc_pt: "Nicho sushi: bancada refrigerada -4°C, cuba shari, show cooking. Poke bowl €12–€16 com 55% margem. Ticket premium.",
        seo_desc_en: "Sushi niche: -4°C counter, shari tub, show cooking. Poke bowl €12–€16 with 55% margin. Premium ticket.",
    },
    pipocas: {
        icon: Sparkles,
        primary: "#B45309",
        accent: "#FDE68A",
        soft: "#FFFBEB",
        label_pt: "Pipocas gourmet",
        label_en: "Gourmet popcorn",
        eyebrow_pt: "Nicho de mercado · Pipocas",
        eyebrow_en: "Market niche · Popcorn",
        title_pt: ["Pipocas retro, ", "cheiro a manteiga", ", fila garantida."],
        title_en: ["Retro popcorn, ", "buttery aroma", ", queue guaranteed."],
        subtitle_pt: "Máquina retro 8oz com iluminação vintage, cones kraft branded e sabores gourmet (trufa, caramelo salgado, cheddar). Baixo investimento, alto impacto visual.",
        subtitle_en: "8oz retro machine with vintage lighting, branded kraft cones and gourmet flavours (truffle, salted caramel, cheddar). Low investment, high visual impact.",
        images: {
            hero: "/brand/tema-pipocas-branded.jpg",
            branded: "/brand/tema-pipocas-branded.jpg",
            product: "/brand/tema-pipocas.jpg",
            no_canopy: "/brand/tema-pipocas-no-canopy.jpg",
        },
        audience_pt: "Famílias em cinemas ao ar livre, feiras, festivais e mercados de Natal.",
        audience_en: "Families at outdoor cinemas, fairs, festivals and Christmas markets.",
        advantages: [
            {
                icon: TrendingUp,
                title_pt: "Margem 500%+",
                title_en: "500%+ margin",
                desc_pt: "Milho custa €0,10 por dose vendida a €3–€5. Ticket puro lucro depois do primeiro dia.",
                desc_en: "Corn costs €0.10 per serving sold at €3–€5. Pure profit after day one.",
            },
            {
                icon: Sparkles,
                title_pt: "Cinema experience",
                title_en: "Cinema experience",
                desc_pt: "Máquina retro vermelho + neon cria fotos instagramáveis — marketing orgânico.",
                desc_en: "Retro red machine + neon creates Instagrammable photos — organic marketing.",
            },
            {
                icon: Zap,
                title_pt: "Investimento baixo",
                title_en: "Low investment",
                desc_pt: "Modelo Alfa + máquina 8oz completa custa menos de €2.500. ROI em 6–8 semanas.",
                desc_en: "Alfa model + full 8oz machine under €2,500. ROI in 6–8 weeks.",
            },
        ],
        equipment_pt: ["Máquina retro 8oz", "Iluminação neon LED", "Cones kraft personalizados", "Bancada de sabores gourmet"],
        equipment_en: ["8oz retro machine", "Neon LED lighting", "Custom kraft cones", "Gourmet flavours station"],
        margin: "€40 – €110",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Cinemas ao ar livre", "Feiras & romarias", "Mercados de Natal", "Parques infantis premium"],
        locations_en: ["Outdoor cinemas", "Fairs & festivals", "Christmas markets", "Premium playgrounds"],
        ideal_model: "alfa",
        seo_desc_pt: "Nicho pipocas gourmet: carrinho com máquina retro 8oz, iluminação neon e cones kraft. Margem 500%+ com investimento abaixo de €2.500.",
        seo_desc_en: "Gourmet popcorn niche: cart with 8oz retro machine, neon lighting and kraft cones. 500%+ margin under €2,500 investment.",
    },
    crepes: {
        icon: CroissantIcon,
        primary: "#78350F",
        accent: "#FCD34D",
        soft: "#FFFBEB",
        label_pt: "Crepes & waffles",
        label_en: "Crêpes & waffles",
        eyebrow_pt: "Nicho de mercado · Crepes",
        eyebrow_en: "Market niche · Crêpes",
        title_pt: ["Crepes ", "à francesa", ", servidos ao minuto."],
        title_en: ["French-style crêpes, ", "served fresh", "."],
        subtitle_pt: "Placa profissional 40 cm, dosificador de massa, bancada de recheios (Nutella, banana, doce de leite) e cones kraft. Perfeito para dusk-to-late em mercados urbanos e Natal.",
        subtitle_en: "40cm professional plate, dough dispenser, filling station (Nutella, banana, dulce de leche) and kraft cones. Perfect for dusk-to-late urban markets and Christmas.",
        images: {
            hero: "/brand/tema-crepes-branded.jpg",
            branded: "/brand/tema-crepes-branded.jpg",
            product: "/brand/tema-crepes.jpg",
            no_canopy: "/brand/tema-crepes-no-canopy.jpg",
        },
        audience_pt: "Casais em passeio, universitários, mercados de Natal e eventos noturnos.",
        audience_en: "Couples strolling, university students, Christmas markets and night events.",
        advantages: [
            {
                icon: Clock,
                title_pt: "Servido em 90 segundos",
                title_en: "Served in 90 seconds",
                desc_pt: "Crepe fresco vira/enrola em 1,5 min. 40+ vendas/hora em pico de mercado.",
                desc_en: "Fresh crêpe flips/rolls in 1.5 min. 40+ sales/hour at market peak.",
            },
            {
                icon: TrendingUp,
                title_pt: "Ticket dulce €4,50",
                title_en: "€4.50 sweet ticket",
                desc_pt: "Crepe Nutella-banana a €4,50 com custo €0,80. Margem líquida 68% consistente.",
                desc_en: "Nutella-banana crêpe at €4.50 with €0.80 cost. Consistent 68% net margin.",
            },
            {
                icon: Sparkles,
                title_pt: "Ambiente noturno",
                title_en: "Night ambience",
                desc_pt: "String-lights + neon dão vibração de mercado de Natal — ideal para dusk-to-late.",
                desc_en: "String-lights + neon give a Christmas-market feel — perfect for dusk-to-late.",
            },
        ],
        equipment_pt: ["Placa crêpe 40 cm profissional", "Dosificador de massa", "Bancada de recheios", "Iluminação neon + string-lights"],
        equipment_en: ["40 cm professional crêpe plate", "Dough dispenser", "Filling station", "Neon + string lighting"],
        margin: "€55 – €140",
        margin_hint_pt: "margem/dia",
        margin_hint_en: "margin/day",
        locations_pt: ["Mercados de Natal", "Baixas históricas à noite", "Festivais & concertos", "Zonas turísticas"],
        locations_en: ["Christmas markets", "Historic centres at night", "Festivals & concerts", "Tourist zones"],
        ideal_model: "alfa",
        seo_desc_pt: "Nicho crepes: carrinho com placa 40cm, dosificador e bancada de recheios. Crepe €4,50 com 68% margem. Dusk-to-late em mercados urbanos.",
        seo_desc_en: "Crêpes niche: cart with 40cm plate, dispenser and filling station. €4.50 crêpe at 68% margin. Dusk-to-late urban markets.",
    },
};

/* Slug aliases (accents / alt spellings) */
const SLUG_ALIASES = {
    "cafés": "cafes",
    "pães": "paes",
    "sanduíches": "sanduiches",
    "sanduiche": "sanduiches",
    "sushi": "sushis",
    "sushi-poke": "sushis",
    "gelado": "gelados",
    "doce": "doces",
    "drink": "drinks",
    "jornal": "jornais",
    "cafe": "cafes",
    "pao": "paes",
    "churro": "churros",
    "pipoca": "pipocas",
    "crepe": "crepes",
};

export function niches() {
    return NICHES;
}

export default function Negocio() {
    const { slug: rawSlug } = useParams();
    const { tr, en, fmtPrice } = useLocale();
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");

    const slug = useMemo(() => {
        if (!rawSlug) return null;
        const s = rawSlug.toLowerCase();
        return NICHES[s] ? s : (SLUG_ALIASES[s] || null);
    }, [rawSlug]);

    const niche = slug ? NICHES[slug] : null;

    /* Compute list of "other niches" for the footer navigation */
    const otherNiches = useMemo(() => {
        if (!slug) return [];
        return Object.entries(NICHES)
            .filter(([id]) => id !== slug)
            .map(([id, data]) => ({ id, ...data }));
    }, [slug]);

    const label = niche ? (en ? niche.label_en : niche.label_pt) : "";
    const title = niche ? (en ? niche.title_en : niche.title_pt) : [];
    const subtitle = niche ? (en ? niche.subtitle_en : niche.subtitle_pt) : "";
    const eyebrow = niche ? (en ? niche.eyebrow_en : niche.eyebrow_pt) : "";
    const audience = niche ? (en ? niche.audience_en : niche.audience_pt) : "";
    const equipment = niche ? (en ? niche.equipment_en : niche.equipment_pt) : [];
    const locations = niche ? (en ? niche.locations_en : niche.locations_pt) : [];
    const marginHint = niche ? (en ? niche.margin_hint_en : niche.margin_hint_pt) : "";
    const seoDesc = niche ? (en ? niche.seo_desc_en : niche.seo_desc_pt) : "";

    useSeo({
        title: niche ? `${label} — NOVIFOOD` : "NOVIFOOD",
        description: seoDesc,
        path: `/negocios/${slug || ""}`,
        image: niche ? niche.images.hero : undefined,
    });

    /* Testimonials for THIS niche — fetched from public endpoint, fallback to
       featured/general testimonials if none specific exists. */
    const [nicheTestimonials, setNicheTestimonials] = useState([]);
    /* Canopy on/off toggle for the gallery — reflects the same option available
       on the Alfa/Ômega product pages. When "sem cobertura" is active, the
       gallery swaps to the no-canopy variant of this niche's photo. */
    const [canopyOn, setCanopyOn] = useState(true);
    useEffect(() => {
        if (!slug || !niche) { setNicheTestimonials([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const specific = await api.get("/testimonials", { params: { niche: slug, limit: 2 } });
                if (cancelled) return;
                if (Array.isArray(specific.data) && specific.data.length > 0) {
                    setNicheTestimonials(specific.data);
                } else {
                    const fallback = await api.get("/testimonials", { params: { featured: true, limit: 1 } });
                    if (!cancelled) setNicheTestimonials(Array.isArray(fallback.data) ? fallback.data : []);
                }
            } catch (e) {
                if (!cancelled) setNicheTestimonials([]);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (!niche) {
        return <Navigate to="/" replace />;
    }

    const Icon = niche.icon;
    const idealModelPath = niche.ideal_model === "omega" ? "/modelo-omega" : "/modelo-alfa";
    const idealModelName = niche.ideal_model === "omega" ? "Modelo Ômega" : "Modelo Alfa";
    const idealModelPrice = niche.ideal_model === "omega" ? 3000 : 2000;

    return (
        <>
            {/* -------- HERO -------- */}
            <section
                className="relative overflow-hidden"
                style={{ background: `linear-gradient(180deg, ${niche.soft} 0%, #FFFFFF 100%)` }}
                data-testid={`niche-hero-${slug}`}
            >
                <div
                    aria-hidden
                    className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl opacity-40"
                    style={{ background: niche.accent }}
                />
                <div className="container-x pt-8 pb-12 lg:pt-16 lg:pb-20 relative">
                    {/* Breadcrumb */}
                    <nav className="mb-6 flex items-center gap-2 text-xs text-muted-foreground" aria-label="Breadcrumb">
                        <Link to="/" className="hover:text-primary transition-colors">
                            {tr("Início", "Home")}
                        </Link>
                        <span>/</span>
                        <Link to="/#nichos" className="hover:text-primary transition-colors">
                            {tr("Nichos", "Niches")}
                        </Link>
                        <span>/</span>
                        <span className="text-primary font-semibold">{label}</span>
                    </nav>

                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        {/* Left column: copy */}
                        <div className="lg:col-span-6 order-2 lg:order-1">
                            <Reveal>
                                <div className="flex items-center gap-3 mb-4">
                                    <span
                                        className="inline-flex h-11 w-11 items-center justify-center rounded-xl shadow-md"
                                        style={{ background: niche.primary, color: "#FFFFFF" }}
                                    >
                                        <Icon className="h-5 w-5" strokeWidth={2} />
                                    </span>
                                    <span className="eyebrow-line" data-testid={`niche-eyebrow-${slug}`}>{eyebrow}</span>
                                </div>
                                <h1 className="display-xl text-primary" data-testid={`niche-title-${slug}`}>
                                    {title[0]}
                                    <span className="serif-accent" style={{ color: niche.primary }}>{title[1]}</span>
                                    {title[2]}
                                </h1>
                                <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl" data-testid={`niche-subtitle-${slug}`}>
                                    {subtitle}
                                </p>

                                {/* Margin badge — "desde €X" (lower bound of the range) */}
                                <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-border bg-white px-5 py-3 shadow-sm">
                                    <TrendingUp className="h-5 w-5" style={{ color: niche.primary }} />
                                    <div>
                                        <div className="font-heading text-2xl font-extrabold text-primary leading-none">
                                            <span className="text-sm font-bold text-primary/60 mr-1">{tr("desde", "from")}</span>
                                            {(niche.margin.match(/€\s*\d[\d.,]*/) || ["€—"])[0]}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">
                                            {marginHint}
                                        </div>
                                    </div>
                                </div>

                                {/* CTAs */}
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <Link
                                        to={`/simulador?tema=${encodeURIComponent(slug)}`}
                                        className="btn-premium-gold"
                                        data-testid={`niche-cta-simulator-${slug}`}
                                    >
                                        {tr("Configurar o meu carrinho", "Configure my cart")}
                                        <ArrowRight className="h-4 w-4 arrow-shift" />
                                    </Link>
                                    <Link
                                        to="/contacto"
                                        className="btn-premium-ghost"
                                        data-testid={`niche-cta-contact-${slug}`}
                                    >
                                        {tr("Falar connosco", "Talk to us")}
                                        <MessageCircle className="h-4 w-4" />
                                    </Link>
                                </div>
                            </Reveal>
                        </div>

                        {/* Right column: hero image */}
                        <div className="lg:col-span-6 order-1 lg:order-2">
                            <Reveal delay={2}>
                                <div className="relative">
                                    {/* L-corner gold accents */}
                                    <div className="absolute -top-3 -left-3 h-8 w-8 border-t-2 border-l-2 border-secondary rounded-tl-xl" aria-hidden />
                                    <div className="absolute -bottom-3 -right-3 h-8 w-8 border-b-2 border-r-2 border-secondary rounded-br-xl" aria-hidden />
                                    <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-border shadow-[0_28px_56px_-20px_rgba(11,37,69,0.28)]">
                                        <img
                                            src={withV(niche.images.hero)}
                                            alt={label}
                                            className="h-full w-full object-cover transition-transform duration-1000 hover:scale-105"
                                            data-testid={`niche-hero-image-${slug}`}
                                            loading="eager"
                                        />
                                    </div>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* -------- ADVANTAGES -------- */}
            <section className="container-x section-y" data-testid={`niche-advantages-${slug}`}>
                <Reveal className="max-w-2xl">
                    <span className="eyebrow-line">{tr("Porquê este negócio", "Why this business")}</span>
                    <h2 className="mt-5 display-lg text-primary">
                        {tr("Vantagens ", "Advantages of ")}
                        <span className="serif-accent" style={{ color: niche.primary }}>
                            {en ? "of this niche" : "deste nicho"}
                        </span>
                        .
                    </h2>
                    <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                        {tr(
                            `Público-alvo: ${niche.audience_pt}`,
                            `Target audience: ${niche.audience_en}`
                        )}
                    </p>
                </Reveal>
                <div className="mt-12 grid gap-6 md:grid-cols-3">
                    {niche.advantages.map((adv, i) => {
                        const AdvIcon = adv.icon;
                        return (
                            <Reveal key={i} delay={Math.min(i + 1, 4)}>
                                <article
                                    className="h-full rounded-2xl bg-white border border-border p-7 lg:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30"
                                    data-testid={`niche-advantage-${slug}-${i}`}
                                >
                                    <div
                                        className="inline-flex h-12 w-12 items-center justify-center rounded-xl"
                                        style={{ background: niche.primary, color: "#FFFFFF" }}
                                    >
                                        <AdvIcon className="h-5 w-5" strokeWidth={1.75} />
                                    </div>
                                    <h3 className="mt-6 font-heading text-2xl lg:text-[26px] font-bold text-primary leading-tight">
                                        {en ? adv.title_en : adv.title_pt}
                                    </h3>
                                    <p className="mt-3 text-sm lg:text-base text-muted-foreground leading-relaxed">
                                        {en ? adv.desc_en : adv.desc_pt}
                                    </p>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* -------- GALLERY: BRANDED VS PRODUCT + CANOPY TOGGLE -------- */}
            <section className="bg-muted/60 border-y border-border" data-testid={`niche-gallery-${slug}`}>
                <div className="container-x section-y">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <Reveal className="max-w-2xl">
                            <span className="eyebrow-line">{tr("Galeria", "Gallery")}</span>
                            <h2 className="mt-5 display-lg text-primary">
                                {tr("Com marca. ", "With brand. ")}
                                <span className="serif-accent">{tr("Ou neutro", "Or neutral")}</span>
                                .{" "}
                                <span className="text-primary/60">{tr("Ambos rendem.", "Both perform.")}</span>
                            </h2>
                            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                                {tr(
                                    "Personalize as cores, o lettering e a canópia para expressar a sua identidade — ou opte por um design neutro premium que se adapta a qualquer ativação.",
                                    "Personalise colours, lettering and canopy to express your identity — or choose a premium neutral design that adapts to any activation."
                                )}
                            </p>
                        </Reveal>
                        {niche.images.no_canopy && (
                            <Reveal delay={1}>
                                <div className="inline-flex rounded-full border border-border bg-white p-1 shadow-sm" role="tablist" aria-label={tr("Cobertura do carrinho", "Cart canopy")} data-testid={`niche-canopy-toggle-${slug}`}>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={canopyOn}
                                        onClick={() => setCanopyOn(true)}
                                        data-testid={`niche-canopy-toggle-on-${slug}`}
                                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                                            canopyOn ? "bg-primary text-primary-foreground shadow-[0_4px_12px_-4px_rgba(11,37,69,0.4)]" : "text-muted-foreground hover:text-primary"
                                        }`}
                                    >
                                        <Umbrella className="h-3.5 w-3.5" strokeWidth={2} />
                                        {tr("Com cobertura", "With canopy")}
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={!canopyOn}
                                        onClick={() => setCanopyOn(false)}
                                        data-testid={`niche-canopy-toggle-off-${slug}`}
                                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                                            !canopyOn ? "bg-primary text-primary-foreground shadow-[0_4px_12px_-4px_rgba(11,37,69,0.4)]" : "text-muted-foreground hover:text-primary"
                                        }`}
                                    >
                                        <Sun className="h-3.5 w-3.5" strokeWidth={2} />
                                        {tr("Sem cobertura", "Open top")}
                                    </button>
                                </div>
                            </Reveal>
                        )}
                    </div>

                    {canopyOn ? (
                        <div className="mt-12 grid gap-6 md:grid-cols-2">
                            <Reveal delay={1}>
                                <figure className="relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
                                    <span
                                        className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md"
                                        style={{ background: niche.primary }}
                                    >
                                        <Sparkles className="h-3 w-3" /> {tr("Com marca", "With brand")}
                                    </span>
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img
                                            src={withV(niche.images.branded)}
                                            alt={`${label} — ${tr("versão com marca", "branded version")}`}
                                            className="h-full w-full object-cover transition-transform duration-1000 hover:scale-105"
                                            loading="lazy"
                                            data-testid={`niche-gallery-branded-${slug}`}
                                        />
                                    </div>
                                    <figcaption className="p-5">
                                        <p className="text-sm text-primary/80 leading-relaxed">
                                            {tr(
                                                "Branding total — canópia com logotipo, lettering e paleta de cores personalizada para dar visibilidade à sua marca.",
                                                "Full branding — canopy with logo, lettering and custom colour palette to give your brand visibility."
                                            )}
                                        </p>
                                    </figcaption>
                                </figure>
                            </Reveal>
                            <Reveal delay={2}>
                                <figure className="relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
                                    <span className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md">
                                        <Package className="h-3 w-3" /> {tr("Sem marca", "Neutral")}
                                    </span>
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img
                                            src={withV(niche.images.product)}
                                            alt={`${label} — ${tr("versão neutra", "neutral version")}`}
                                            className="h-full w-full object-cover transition-transform duration-1000 hover:scale-105"
                                            loading="lazy"
                                            data-testid={`niche-gallery-product-${slug}`}
                                        />
                                    </div>
                                    <figcaption className="p-5">
                                        <p className="text-sm text-primary/80 leading-relaxed">
                                            {tr(
                                                "Acabamento premium neutro — perfeito para aluguer a marcas parceiras ou operações multi-tema com um único investimento.",
                                                "Neutral premium finish — perfect for renting to partner brands or multi-theme operations with a single investment."
                                            )}
                                        </p>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        </div>
                    ) : (
                        <Reveal delay={1} className="mt-12">
                            <figure className="relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm max-w-4xl mx-auto">
                                <span
                                    className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-md bg-primary"
                                >
                                    <Sun className="h-3 w-3" /> {tr("Sem cobertura · topo aberto", "Open top · no canopy")}
                                </span>
                                <div className="aspect-[3/2] overflow-hidden">
                                    <img
                                        src={withV(niche.images.no_canopy)}
                                        alt={`${label} — ${tr("versão sem cobertura", "no-canopy version")}`}
                                        className="h-full w-full object-cover transition-transform duration-1000 hover:scale-105"
                                        loading="lazy"
                                        data-testid={`niche-gallery-no-canopy-${slug}`}
                                    />
                                </div>
                                <figcaption className="p-5">
                                    <p className="text-sm text-primary/80 leading-relaxed">
                                        {tr(
                                            "Configuração aberta — sem toldo de tecido, com os painéis solares e a estrutura de alumínio à vista. Ideal para eventos indoor, food halls e activações onde o teto não é necessário.",
                                            "Open configuration — no fabric awning, exposing the solar panels and the aluminium frame. Ideal for indoor events, food halls and activations where a roof is not required."
                                        )}
                                    </p>
                                </figcaption>
                            </figure>
                        </Reveal>
                    )}
                </div>
            </section>

            {/* -------- EQUIPMENT + LOCATIONS -------- */}
            <section className="container-x section-y" data-testid={`niche-details-${slug}`}>
                <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
                    <Reveal>
                        <span className="eyebrow-line">{tr("Equipamento sugerido", "Suggested equipment")}</span>
                        <h3 className="mt-5 font-heading text-3xl lg:text-4xl font-extrabold text-primary leading-tight">
                            {tr("Tudo o que precisa, ", "Everything you need, ")}
                            <span className="serif-accent" style={{ color: niche.primary }}>{tr("já dentro", "already inside")}</span>.
                        </h3>
                        <ul className="mt-6 space-y-3">
                            {equipment.map((eq, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 rounded-xl border border-border bg-white p-4 transition-colors hover:border-secondary/40"
                                    data-testid={`niche-equipment-${slug}-${i}`}
                                >
                                    <span
                                        className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                                        style={{ background: niche.soft, color: niche.primary }}
                                    >
                                        <Ruler className="h-3.5 w-3.5" />
                                    </span>
                                    <span className="text-sm lg:text-base text-primary/90 font-medium">{eq}</span>
                                </li>
                            ))}
                        </ul>

                        {/* Ideal model */}
                        <div className="mt-8 rounded-2xl border border-border bg-white p-5 lg:p-6">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                                <Award className="h-3.5 w-3.5" style={{ color: niche.primary }} />
                                {tr("Modelo recomendado", "Recommended model")}
                            </div>
                            <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                                <span className="font-heading text-2xl font-extrabold text-primary">{idealModelName}</span>
                                <span className="font-heading text-lg font-bold text-primary/60">
                                    {tr("desde", "from")} <span className="serif-accent">{fmtPrice(idealModelPrice)}</span>
                                </span>
                            </div>
                            <Link
                                to={idealModelPath}
                                className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary hover:text-secondary transition-colors"
                                data-testid={`niche-ideal-model-${slug}`}
                            >
                                {tr(`Ver ${idealModelName}`, `View ${idealModelName}`)}
                                <ArrowUpRight className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    </Reveal>

                    <Reveal delay={1}>
                        <span className="eyebrow-line">{tr("Onde instalar", "Where to install")}</span>
                        <h3 className="mt-5 font-heading text-3xl lg:text-4xl font-extrabold text-primary leading-tight">
                            {tr("Localizações que ", "Locations that ")}
                            <span className="serif-accent" style={{ color: niche.primary }}>{tr("funcionam", "work")}</span>.
                        </h3>
                        <p className="mt-4 text-sm lg:text-base text-muted-foreground leading-relaxed">
                            {tr(
                                "Baseado em dados dos nossos clientes atuais — locais onde este nicho tem melhor performance de fluxo e ticket médio.",
                                "Based on data from our current clients — spots where this niche performs best in traffic and average ticket."
                            )}
                        </p>
                        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                            {locations.map((loc, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 rounded-xl p-4 border border-border bg-white transition-all hover:-translate-y-0.5 hover:shadow-md"
                                    data-testid={`niche-location-${slug}-${i}`}
                                >
                                    <MapPin className="h-4 w-4 shrink-0 mt-0.5" style={{ color: niche.primary }} />
                                    <span className="text-sm text-primary/90">{loc}</span>
                                </li>
                            ))}
                        </ul>

                        {/* Audience card */}
                        <div className="mt-8 rounded-2xl p-5 lg:p-6 border" style={{ background: niche.soft, borderColor: niche.accent }}>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-widest" style={{ color: niche.primary }}>
                                <Users className="h-3.5 w-3.5" />
                                {tr("Público-alvo", "Target audience")}
                            </div>
                            <p className="mt-3 text-base text-primary leading-relaxed font-medium">
                                {audience}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* -------- TESTIMONIALS FOR THIS NICHE -------- */}
            {nicheTestimonials.length > 0 && (
                <section className="bg-muted/40 border-y border-border" data-testid={`niche-testimonials-${slug}`}>
                    <div className="container-x section-y">
                        <Reveal className="max-w-2xl">
                            <span className="eyebrow-line">{tr("Provas reais", "Real proof")}</span>
                            <h3 className="mt-5 font-heading text-3xl lg:text-4xl font-extrabold text-primary leading-tight">
                                {tr("O que dizem quem já ", "What operators who already ")}
                                <span className="serif-accent" style={{ color: niche.primary }}>
                                    {tr("opera neste nicho", "run this niche")}
                                </span>.
                            </h3>
                        </Reveal>
                        <div className={`mt-12 grid gap-6 ${nicheTestimonials.length === 1 ? "max-w-2xl mx-auto" : "md:grid-cols-2"}`}>
                            {nicheTestimonials.slice(0, 2).map((t) => (
                                <Reveal key={t.id} delay={1}>
                                    <figure
                                        className="relative h-full rounded-2xl border border-border bg-white p-7 lg:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30"
                                        data-testid={`niche-testimonial-${slug}-${t.id}`}
                                    >
                                        <Quote
                                            className="absolute top-5 right-5 h-8 w-8 opacity-15"
                                            style={{ color: niche.primary }}
                                            aria-hidden
                                        />
                                        <blockquote className="text-primary/90 leading-relaxed text-base lg:text-lg italic font-medium">
                                            &ldquo;{t.quote}&rdquo;
                                        </blockquote>
                                        <figcaption className="mt-6 flex items-center gap-3">
                                            {t.photo_url ? (
                                                <img
                                                    src={t.photo_url}
                                                    alt={t.name}
                                                    className="h-12 w-12 rounded-full object-cover ring-2 ring-secondary/40"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div
                                                    className="h-12 w-12 rounded-full flex items-center justify-center font-heading font-bold text-white"
                                                    style={{ background: niche.primary }}
                                                >
                                                    {(t.name || "").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-heading font-bold text-primary">{t.name}</div>
                                                <div className="text-xs text-muted-foreground">{t.role}</div>
                                            </div>
                                        </figcaption>
                                    </figure>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* -------- FINAL CTA -------- */}
            <section className="container-x pb-12 lg:pb-20">
                <CtaBanner
                    eyebrow={en ? `Ready for the ${label} business?` : `Pronto para o negócio de ${niche.label_pt.toLowerCase()}?`}
                    title={tr("Configure hoje. Recebe orçamento em 48h.", "Configure today. Get a quote in 48h.")}
                    description={tr(
                        "Escolha equipamento, cores e canópia no simulador — ou fale directamente com a equipa para um briefing dedicado a este nicho.",
                        "Choose equipment, colours and canopy in the simulator — or talk directly to our team for a briefing dedicated to this niche."
                    )}
                    primary={{ to: `/simulador?tema=${encodeURIComponent(slug)}`, label: tr("Configurar no simulador", "Configure it yourself") }}
                    secondary={{
                        to: `https://wa.me/${whatsappNumber}`,
                        label: tr("Falar no WhatsApp", "Chat on WhatsApp"),
                        external: true,
                        icon: MessageCircle,
                    }}
                    variant="navy"
                    testId={`niche-cta-final-${slug}`}
                />
            </section>

            {/* -------- OTHER NICHES CAROUSEL -------- */}
            <section className="bg-muted/40 border-t border-border">
                <div className="container-x section-y">
                    <Reveal className="max-w-2xl">
                        <span className="eyebrow-line">{tr("Explorar mais nichos", "Explore more niches")}</span>
                        <h3 className="mt-5 font-heading text-3xl lg:text-4xl font-extrabold text-primary leading-tight">
                            {tr("Um carrinho, ", "One cart, ")}
                            <span className="serif-accent">{tr("infinitas ideias", "endless ideas")}</span>.
                        </h3>
                    </Reveal>
                    <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        {otherNiches.map((n) => {
                            const NIcon = n.icon;
                            return (
                                <Link
                                    key={n.id}
                                    to={`/negocios/${n.id}`}
                                    className="group relative flex flex-col gap-3 rounded-2xl border border-border bg-white p-4 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-lg hover:border-secondary/40"
                                    data-testid={`niche-other-${n.id}`}
                                >
                                    <div className="aspect-[4/3] overflow-hidden rounded-xl">
                                        <img
                                            src={withV(n.images.hero)}
                                            alt={en ? n.label_en : n.label_pt}
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="inline-flex h-6 w-6 items-center justify-center rounded-md"
                                            style={{ background: n.primary, color: "#FFFFFF" }}
                                        >
                                            <NIcon className="h-3 w-3" />
                                        </span>
                                        <span className="font-heading text-sm font-extrabold text-primary truncate">
                                            {en ? n.label_en : n.label_pt}
                                        </span>
                                        <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-muted-foreground group-hover:text-secondary transition-colors" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
}
